// apps/admin-ui/src/components/gauges/ReserveRatioGauge.tsx
// Static SVG gauge for reserve ratio display
// Zero JavaScript - pure CSS/SVG for instant render
// ALL RIGHTS RESERVED

import '../../styles/gauges.css';

export interface ReserveRatioGaugeProps {
  ratio: number;      // Current reserve ratio (e.g., 2.04)
  minRatio: number;   // Minimum required ratio (e.g., 2.0)
}

/**
 * Reserve Ratio Gauge
 * 
 * Displays reserve ratio as a semicircular gauge.
 * Uses static SVG - no JavaScript animations or timers.
 * 
 * Visual scale: 0:1 to 3:1 (display range)
 * Compliance: Green if ratio >= minRatio, warning otherwise
 */
export function ReserveRatioGauge({ ratio, minRatio }: ReserveRatioGaugeProps) {
  // Clamp ratio to display range (0 to 3)
  const clampedRatio = Math.max(0, Math.min(3, ratio));
  
  // Calculate percentage for gauge fill (0-100%)
  const percentage = (clampedRatio / 3) * 100;
  
  // Arc path calculations
  // Semicircle from 180° to 0° (left to right)
  // Full arc length: ~125.6 (π * radius * 2 / 2)
  const arcLength = 125.6;
  const fillLength = (arcLength * percentage) / 100;
  const dashOffset = arcLength - fillLength;
  
  // Determine color based on compliance
  const isCompliant = ratio >= minRatio;
  const gaugeColor = isCompliant ? 'var(--color-success)' : 'var(--color-warning)';
  
  return (
    <div className="gauge gauge--reserve-ratio">
      <svg viewBox="0 0 100 55" className="gauge-svg">
        {/* Background arc */}
        <path
          d="M10,50 A40,40 0 0,1 90,50"
          fill="none"
          stroke="var(--color-bg-muted)"
          strokeWidth="8"
          strokeLinecap="round"
        />
        
        {/* Filled arc */}
        <path
          d="M10,50 A40,40 0 0,1 90,50"
          fill="none"
          stroke={gaugeColor}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={arcLength}
          strokeDashoffset={dashOffset}
          className="gauge-fill"
        />
        
        {/* Min ratio marker (at 2:1 = 66.67%) */}
        <circle
          cx={50 + 40 * Math.cos(Math.PI * (1 - (minRatio / 3)))}
          cy={50 - 40 * Math.sin(Math.PI * (minRatio / 3))}
          r="3"
          fill="var(--color-text-muted)"
          className="gauge-marker"
        />
        
        {/* Value text */}
        <text
          x="50"
          y="42"
          textAnchor="middle"
          className="gauge-value"
          fill="var(--color-text)"
        >
          {ratio.toFixed(2)}
        </text>
        <text
          x="50"
          y="52"
          textAnchor="middle"
          className="gauge-unit"
          fill="var(--color-text-muted)"
        >
          :1
        </text>
      </svg>
    </div>
  );
}

export default ReserveRatioGauge;
